// TypeScript port of backend/vtlite/staircase.py (Staircase, QuestStaircase, DoubleStaircase,
// InterleavedStaircases).
// It must reproduce shared/golden/staircase_vectors.json exactly: change both or neither.

import { choice } from "./rng";

export type StaircaseState = "running" | "complete";

export interface SubStaircase {
  readonly label: string;
  readonly minValue: number;
  readonly maxValue: number;
  readonly currentValue: number;
  readonly state: StaircaseState;
  readonly trialCount: number;
  readonly reversalCount: number;
  feedResponse(correct: boolean, presentedValue?: number): void;
  threshold(): number | null;
  /** End it now (it has settled). */
  finish(): void;
}

export interface StaircaseOptions {
  startValue: number;
  stepSize: number;
  minValue: number;
  maxValue: number;
  nDown?: number;
  nUp?: number;
  maxReversals?: number;
  reversalsToAverage?: number;
  stepFactor?: number;
  label?: string;
  stepUpSize?: number | null;
  stepDownSize?: number | null;
  /** Also stop after this many trials (null: reversals only). */
  maxTrials?: number | null;
  /** Reversal counts at which the steps shrink by stepFactor (default: the 2nd). */
  reduceAtReversals?: number[];
}

/** N-down / M-up transformed staircase (Levitt 1971); asymmetric steps give Kaernbach 1991. */
export class Staircase implements SubStaircase {
  readonly startValue: number;
  readonly stepSize: number;
  readonly stepUpSize: number;
  readonly stepDownSize: number;
  readonly minValue: number;
  readonly maxValue: number;
  readonly nDown: number;
  readonly nUp: number;
  readonly maxReversals: number;
  readonly reversalsToAverage: number;
  readonly stepFactor: number;
  readonly label: string;
  readonly maxTrials: number | null;
  readonly reduceAtReversals: number[];

  private _current: number;
  private _step: number;
  private _stepUp: number;
  private _stepDown: number;
  private _consecutiveCorrect = 0;
  private _consecutiveIncorrect = 0;
  private _lastDirection: "up" | "down" | null = null;
  private _reversalValues: number[] = [];
  private _nReversals = 0;
  private _trialCount = 0;
  private _state: StaircaseState = "running";

  constructor(o: StaircaseOptions) {
    this.startValue = o.startValue;
    this.stepSize = o.stepSize;
    this.stepUpSize = o.stepUpSize ?? o.stepSize;
    this.stepDownSize = o.stepDownSize ?? o.stepSize;
    this.minValue = o.minValue;
    this.maxValue = o.maxValue;
    this.nDown = o.nDown ?? 3;
    this.nUp = o.nUp ?? 1;
    this.maxReversals = o.maxReversals ?? 12;
    this.reversalsToAverage = o.reversalsToAverage ?? 8;
    this.stepFactor = o.stepFactor ?? 0.5;
    this.label = o.label ?? "";
    this.maxTrials = o.maxTrials ?? null;
    this.reduceAtReversals = o.reduceAtReversals ?? [2];
    this._current = o.startValue;
    this._step = o.stepSize;
    this._stepUp = this.stepUpSize;
    this._stepDown = this.stepDownSize;
  }

  get currentValue(): number { return this._current; }
  get state(): StaircaseState { return this._state; }
  finish(): void { this._state = "complete"; }
  get reversalCount(): number { return this._nReversals; }
  get trialCount(): number { return this._trialCount; }
  get reversalValues(): number[] { return [...this._reversalValues]; }
  get currentStep(): number { return this._step; }

  feedResponse(correct: boolean, _presentedValue?: number): void {
    if (this._state === "complete") return;
    this._trialCount += 1;
    if (correct) {
      this._consecutiveCorrect += 1;
      this._consecutiveIncorrect = 0;
      if (this._consecutiveCorrect >= this.nDown) {
        this.stepDown();
        this._consecutiveCorrect = 0;
      }
    } else {
      this._consecutiveIncorrect += 1;
      this._consecutiveCorrect = 0;
      if (this._consecutiveIncorrect >= this.nUp) {
        this.stepUp();
        this._consecutiveIncorrect = 0;
      }
    }
    if (this.maxTrials !== null && this._trialCount >= this.maxTrials) this._state = "complete";
  }

  private stepDown(): void {
    if (this._lastDirection === "up") this.recordReversal();
    this._lastDirection = "down";
    this._current = Math.max(this.minValue, this._current - this._stepDown);
  }

  private stepUp(): void {
    if (this._lastDirection === "down") this.recordReversal();
    this._lastDirection = "up";
    this._current = Math.min(this.maxValue, this._current + this._stepUp);
  }

  private recordReversal(): void {
    this._nReversals += 1;
    this._reversalValues.push(this._current);
    if (this.reduceAtReversals.includes(this._nReversals)) {
      this._step *= this.stepFactor;
      this._stepUp *= this.stepFactor;
      this._stepDown *= this.stepFactor;
    }
    if (this._nReversals >= this.maxReversals) this._state = "complete";
  }

  threshold(): number | null {
    if (this._reversalValues.length === 0) return null;
    const k = Math.min(this.reversalsToAverage, this._reversalValues.length);
    const vals = this._reversalValues.slice(-k);
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }
}

export interface QuestOptions {
  startValue: number;
  minValue: number;
  maxValue: number;
  maxTrials?: number;
  priorMean?: number | null;
  priorSd?: number | null;
  beta?: number;
  gamma?: number;
  delta?: number;
  grain?: number;
  nInitialEasy?: number;
  label?: string;
  descentStep?: number | null;
}

/** QUEST-style Bayesian staircase (Watson & Pelli 1983), linear in its parameter. */
export class QuestStaircase implements SubStaircase {
  readonly startValue: number;
  readonly minValue: number;
  readonly maxValue: number;
  readonly maxTrials: number;
  readonly label: string;
  readonly descentStep: number | null;
  readonly gamma: number;
  readonly delta: number;
  readonly nInitialEasy: number;
  readonly beta: number;
  readonly priorMean: number;
  readonly priorSd: number;
  readonly grid: Float64Array;

  private logPosterior: Float64Array;
  private readonly logPriorBackup: Float64Array;
  private _current: number;
  private _trialCount = 0;
  private _state: StaircaseState = "running";
  private _inGeometricPhase = true;
  readonly history: Array<[number, boolean]> = [];

  constructor(o: QuestOptions) {
    this.startValue = o.startValue;
    this.minValue = o.minValue;
    this.maxValue = o.maxValue;
    this.maxTrials = o.maxTrials ?? 40;
    this.label = o.label ?? "";
    this.descentStep = o.descentStep ?? null;
    this.gamma = o.gamma ?? 0.25;
    this.delta = o.delta ?? 0.02;
    this.nInitialEasy = o.nInitialEasy ?? 3;

    let range = o.maxValue - o.minValue;
    if (range <= 0) range = 1.0;
    const priorMean = o.priorMean ?? o.minValue + range / 2;
    const priorSd = o.priorSd ?? range / 2;
    let beta = o.beta ?? 0;
    if (beta === 0) beta = 20.0 / range;
    let grain = o.grain ?? 0;
    if (grain === 0) grain = range / 200.0;
    this.beta = beta;
    this.priorMean = priorMean;
    this.priorSd = priorSd;

    this.grid = numpyArange(o.minValue, o.maxValue + grain / 2, grain);
    if (this.grid.length < 3) this.grid = numpyLinspace(o.minValue, o.maxValue, 200);

    const lp = new Float64Array(this.grid.length);
    for (let i = 0; i < lp.length; i++) {
      const z = (this.grid[i]! - priorMean) / priorSd;
      lp[i] = -0.5 * z * z;
    }
    subtractMax(lp);
    this.logPosterior = lp;
    this.logPriorBackup = lp.slice();
    this._current = o.startValue;
  }

  get currentValue(): number { return this._current; }
  get state(): StaircaseState { return this._state; }
  finish(): void { this._state = "complete"; }
  get trialCount(): number { return this._trialCount; }
  get reversalCount(): number { return 0; }

  private psi(x: number, t: number): number {
    return this.gamma + (1.0 - this.gamma - this.delta) / (1.0 + Math.exp(-this.beta * (x - t)));
  }

  feedResponse(correct: boolean, presentedValue?: number): void {
    if (this._state === "complete") return;
    this._trialCount += 1;
    const x = presentedValue ?? this._current;
    this.history.push([x, correct]);
    this.updatePosterior(correct, x);

    // Phase 1: initial easy trials
    if (this._trialCount <= this.nInitialEasy) {
      this._current = this._trialCount < this.nInitialEasy ? this.maxValue : this.startValue;
      if (this._trialCount >= this.maxTrials) this._state = "complete";
      return;
    }
    // Phase 2: rapid descent
    if (this._inGeometricPhase) {
      if (correct) {
        const nxt = this.descentStep !== null ? this._current - this.descentStep : this._current * 0.5;
        this._current = Math.max(nxt, this.minValue);
        if (this._current <= this.minValue) {
          this._inGeometricPhase = false;
          this._current = clip(this.posteriorMean(), this.minValue, this.maxValue);
        }
      } else {
        this._inGeometricPhase = false;
        this._current = clip(this.posteriorMean(), this.minValue, this.maxValue);
      }
      if (this._trialCount >= this.maxTrials) this._state = "complete";
      return;
    }
    // Phase 3: posterior mean
    this._current = clip(this.posteriorMean(), this.minValue, this.maxValue);
    if (this._trialCount >= this.maxTrials) this._state = "complete";
  }

  private updatePosterior(correct: boolean, x: number): void {
    const lp = this.logPosterior;
    let finite = true;
    for (let i = 0; i < lp.length; i++) {
      let p = this.psi(x, this.grid[i]!);
      p = Math.min(Math.max(p, 1e-12), 1.0 - 1e-12);
      lp[i] = lp[i]! + (correct ? Math.log(p) : Math.log(1.0 - p));
    }
    subtractMax(lp);
    for (let i = 0; i < lp.length; i++) if (!Number.isFinite(lp[i]!)) finite = false;
    if (!finite) this.logPosterior = this.logPriorBackup.slice();
  }

  posteriorMean(): number {
    const lp = this.logPosterior;
    const m = maxOf(lp);
    const post = new Float64Array(lp.length);
    for (let i = 0; i < lp.length; i++) post[i] = Math.exp(lp[i]! - m);
    const total = pairwiseSum(post);
    if (total === 0) return this.grid[Math.floor(this.grid.length / 2)]!;
    return dot(post, this.grid) / total;
  }

  posteriorSd(): number {
    const lp = this.logPosterior;
    const m = maxOf(lp);
    const post = new Float64Array(lp.length);
    for (let i = 0; i < lp.length; i++) post[i] = Math.exp(lp[i]! - m);
    const total = pairwiseSum(post);
    for (let i = 0; i < post.length; i++) post[i] = post[i]! / total;
    const mean = dot(post, this.grid);
    const sq = new Float64Array(post.length);
    for (let i = 0; i < sq.length; i++) sq[i] = (this.grid[i]! - mean) ** 2;
    const variance = dot(post, sq);
    return Math.sqrt(Math.max(variance, 0));
  }

  threshold(): number | null {
    if (this._trialCount === 0) return null;
    return this.posteriorMean();
  }
}

export interface DoubleParams {
  method: "quest" | "levitt" | "weighted" | string;
  paramName?: string;
  startLow: number;
  startHigh: number;
  stepSize: number;
  minValue: number;
  maxValue: number;
  nDown?: number;
  nUp?: number;
  maxReversals?: number;
  reversalsToAverage?: number;
  stepFactor?: number;
  stepUpSize?: number | null;
  stepDownSize?: number | null;
  maxTrials?: number;
  priorMean?: number | null;
  priorSd?: number | null;
  beta?: number;
  gamma?: number;
  delta?: number;
  grain?: number;
  nInitialEasy?: number;
  descentStep?: number | null;
}

/** Two interleaved staircases, picked at random each trial. */
export class DoubleStaircase {
  readonly method: string;
  readonly ascending: SubStaircase;
  readonly descending: SubStaircase;
  private active: SubStaircase | null = null;

  constructor(readonly params: DoubleParams, private readonly rng: () => number) {
    this.method = params.method;
    if (params.method === "quest") {
      const common: QuestOptions = {
        startValue: params.maxValue,
        minValue: params.minValue,
        maxValue: params.maxValue,
        maxTrials: params.maxTrials ?? 40,
        priorMean: params.priorMean ?? null,
        priorSd: params.priorSd ?? null,
        beta: params.beta ?? 0,
        gamma: params.gamma ?? 0.25,
        delta: params.delta ?? 0.02,
        grain: params.grain ?? 0,
        nInitialEasy: params.nInitialEasy ?? 3,
        descentStep: params.descentStep ?? null,
      };
      this.ascending = new QuestStaircase({ ...common, label: "Q1" });
      this.descending = new QuestStaircase({ ...common, label: "Q2" });
    } else {
      const common = {
        stepSize: params.stepSize,
        minValue: params.minValue,
        maxValue: params.maxValue,
        nDown: params.nDown ?? 3,
        nUp: params.nUp ?? 1,
        maxReversals: params.maxReversals ?? 12,
        reversalsToAverage: params.reversalsToAverage ?? 8,
        stepFactor: params.stepFactor ?? 0.5,
        stepUpSize: params.stepUpSize ?? null,
        stepDownSize: params.stepDownSize ?? null,
      };
      this.ascending = new Staircase({ ...common, startValue: params.startLow, label: "ascending" });
      this.descending = new Staircase({ ...common, startValue: params.startHigh, label: "descending" });
    }
  }

  get isComplete(): boolean {
    return this.ascending.state === "complete" && this.descending.state === "complete";
  }

  nextTrial(): { value: number; sc: SubStaircase } {
    const candidates: SubStaircase[] = [];
    if (this.ascending.state === "running") candidates.push(this.ascending);
    if (this.descending.state === "running") candidates.push(this.descending);
    if (candidates.length === 0) {
      this.active = this.descending;
      return { value: this.descending.currentValue, sc: this.descending };
    }
    const sc = choice(this.rng, candidates);
    this.active = sc;
    return { value: sc.currentValue, sc };
  }

  get activeStaircase(): SubStaircase | null {
    return this.active;
  }

  feedResponse(sc: SubStaircase, correct: boolean, presentedValue?: number): void {
    sc.feedResponse(correct, presentedValue);
  }

  sub(label: string): SubStaircase {
    if (this.ascending.label === label) return this.ascending;
    if (this.descending.label === label) return this.descending;
    throw new Error(`unknown staircase ${label}`);
  }

  threshold(): number | null {
    const a = this.ascending.threshold();
    const d = this.descending.threshold();
    if (a !== null && d !== null) return (a + d) / 2;
    return a ?? d;
  }

  get totalTrials(): number {
    return this.ascending.trialCount + this.descending.trialCount;
  }

  get totalReversals(): number {
    return this.ascending.reversalCount + this.descending.reversalCount;
  }
}

export interface InterleavedParams {
  method: "quest" | "levitt" | "weighted" | "simple" | string;
  paramName?: string;
  /** One start per staircase (labels S1..Sn); lower values are harder. */
  starts: number[];
  minValue: number;
  maxValue: number;
  maxTrials: number;
  stepSize?: number;
  stepFactor?: number;
  nDown?: number;
  nUp?: number;
  stepUpSize?: number | null;
  stepDownSize?: number | null;
  reduceAtReversals?: number[];
  discardReversals?: number;
  priorMean?: number | null;
  priorSd?: number | null;
  beta?: number;
  gamma?: number;
  delta?: number;
  grain?: number;
  nInitialEasy?: number;
  descentStep?: number | null;
  /** Planning (planNext): see the Python class. */
  stagger?: "none" | "fixed" | "random";
  staggerTrials?: number;
  pick?: "random" | "balanced";
  stopRule?: "trials" | "settled";
  stopReversals?: number;
  stopSd?: number;
  catchRate?: number;
  catchValue?: number;
}

export type PlannedTrial =
  | { catch: true; value: number; sc: null }
  | { catch: false; value: number; sc: SubStaircase };

const rngIndex = (rng: () => number, n: number) => Math.min(n - 1, Math.floor(rng() * n));

/** Any number of staircases of one kind, each trial given to a running one picked at
 *  random; threshold = the mean of theirs (see the Python class for the rules). */
export class InterleavedStaircases {
  readonly method: string;
  readonly subs: SubStaircase[] = [];
  readonly discardReversals: number;
  private readonly presented = new Map<string, number[]>();
  private readonly reversalPresented = new Map<string, number[]>();
  private active: SubStaircase | null = null;
  // planning state (planNext), as in the Python class
  readonly stagger: "none" | "fixed" | "random";
  readonly staggerTrials: number;
  readonly pick: "random" | "balanced";
  readonly stopRule: "trials" | "settled";
  readonly stopReversals: number;
  readonly stopSd: number;
  readonly catchRate: number;
  readonly catchValue: number;
  catchTotal = 0;
  catchCorrect = 0;
  readonly joinAt = new Map<string, number>();
  joinOrder: string[] = [];
  private mainGiven = 0;
  private lastLabel: string | null = null;
  private lastWasCatch = false;
  private readonly roundUsed = new Set<string>();

  constructor(readonly params: InterleavedParams, private readonly rng: () => number) {
    if (params.starts.length === 0) throw new Error("at least one staircase");
    this.method = params.method;
    this.discardReversals = params.discardReversals ?? 2;
    this.stagger = params.stagger ?? "none";
    this.staggerTrials = Math.max(1, Math.trunc(params.staggerTrials ?? 3));
    this.pick = params.pick ?? "random";
    this.stopRule = params.stopRule ?? "trials";
    this.stopReversals = Math.max(1, Math.trunc(params.stopReversals ?? 6));
    this.stopSd = params.stopSd ?? 0.08;
    this.catchRate = params.catchRate ?? 0;
    this.catchValue = params.catchValue ?? 0;
    params.starts.forEach((s, i) => {
      const start = Math.min(Math.max(s, params.minValue), params.maxValue);
      const label = `S${i + 1}`;
      if (params.method === "quest") {
        this.subs.push(new QuestStaircase({
          startValue: start, minValue: params.minValue, maxValue: params.maxValue,
          maxTrials: params.maxTrials, priorMean: params.priorMean ?? null, priorSd: params.priorSd ?? null,
          beta: params.beta ?? 0, gamma: params.gamma ?? 0.25, delta: params.delta ?? 0.02,
          grain: params.grain ?? 0, nInitialEasy: params.nInitialEasy ?? 1,
          descentStep: params.descentStep ?? null, label,
        }));
      } else {
        this.subs.push(new Staircase({
          startValue: start, stepSize: params.stepSize ?? 0.15, minValue: params.minValue,
          maxValue: params.maxValue, nDown: params.nDown ?? 1, nUp: params.nUp ?? 1,
          maxReversals: 1e6, reversalsToAverage: 1e6, stepFactor: params.stepFactor ?? 0.5, label,
          stepUpSize: params.stepUpSize ?? null, stepDownSize: params.stepDownSize ?? null,
          maxTrials: params.maxTrials, reduceAtReversals: params.reduceAtReversals ?? [1, 2],
        }));
      }
      this.presented.set(label, []);
      this.reversalPresented.set(label, []);
      this.joinAt.set(label, 0);
    });
    this.joinOrder = this.subs.map((sc) => sc.label);
    if (this.stagger !== "none" && this.subs.length > 1) {
      const order = this.subs.map((_, i) => i);
      for (let i = order.length - 1; i > 0; i--) {          // Fisher-Yates, as in Python
        const j = rngIndex(this.rng, i + 1);
        [order[i], order[j]] = [order[j]!, order[i]!];
      }
      let at = 0;
      order.forEach((idx, k) => {
        if (k > 0) {
          at += this.stagger === "fixed" ? this.staggerTrials : 1 + rngIndex(this.rng, 2 * this.staggerTrials - 1);
        }
        this.joinAt.set(this.subs[idx]!.label, at);
      });
      this.joinOrder = order.map((i) => this.subs[i]!.label);
    }
  }

  /** Running staircases that have joined; if none has but some wait, the next to join. */
  eligible(): SubStaircase[] {
    const running = this.subs.filter((sc) => sc.state === "running");
    const joined = running.filter((sc) => this.joinAt.get(sc.label)! <= this.mainGiven);
    if (joined.length || !running.length) return joined;
    const first = Math.min(...running.map((sc) => this.joinAt.get(sc.label)!));
    return running.filter((sc) => this.joinAt.get(sc.label) === first);
  }

  /** The next trial, from the seeded planner: an attention check or a staircase trial. */
  planNext(): PlannedTrial {
    if (this.catchRate > 0 && !this.lastWasCatch) {
      if (this.rng() < this.catchRate) {
        this.lastWasCatch = true;
        this.active = null;
        return { catch: true, value: this.catchValue, sc: null };
      }
    }
    this.lastWasCatch = false;
    const pool = this.eligible();
    let sc: SubStaircase;
    if (!pool.length) {
      sc = this.subs[this.subs.length - 1]!;
    } else if (this.pick === "balanced") {
      let bag = pool.filter((x) => !this.roundUsed.has(x.label));
      if (!bag.length) {
        this.roundUsed.clear();
        bag = [...pool];
      }
      const fresh = bag.filter((x) => x.label !== this.lastLabel);
      const choices = fresh.length ? fresh : bag;
      sc = choices[rngIndex(this.rng, choices.length)]!;
      this.roundUsed.add(sc.label);
    } else {
      sc = pool[rngIndex(this.rng, pool.length)]!;
    }
    this.lastLabel = sc.label;
    this.active = sc;
    return { catch: false, value: sc.currentValue, sc };
  }

  /** An attention trial's answer: counted, fed to no staircase. */
  feedCatch(correct: boolean): void {
    this.catchTotal += 1;
    if (correct) this.catchCorrect += 1;
  }

  private settled(sc: SubStaircase): boolean {
    if (sc instanceof QuestStaircase) return sc.trialCount >= 2 && sc.posteriorSd() <= this.stopSd;
    return sc.reversalCount - this.discardReversals >= this.stopReversals;
  }

  get isComplete(): boolean {
    return this.subs.every((sc) => sc.state === "complete");
  }

  nextTrial(): { value: number; sc: SubStaircase } {
    const running = this.subs.filter((sc) => sc.state === "running");
    const sc = running.length ? choice(this.rng, running) : this.subs[this.subs.length - 1]!;
    this.active = sc;
    return { value: sc.currentValue, sc };
  }

  get activeStaircase(): SubStaircase | null {
    return this.active;
  }

  feedResponse(sc: SubStaircase, correct: boolean, presentedValue?: number): void {
    if (sc.state !== "running") return;
    // nothing drawn (a contrast below one code step) counts as the hardest value allowed
    const shown = Math.max(presentedValue ?? sc.currentValue, sc.minValue);
    this.presented.get(sc.label)!.push(shown);
    const before = sc.reversalCount;
    sc.feedResponse(correct, presentedValue);
    this.mainGiven += 1;
    if (sc.reversalCount > before) this.reversalPresented.get(sc.label)!.push(shown);
    if (this.stopRule === "settled" && sc.state === "running" && this.settled(sc)) sc.finish();
  }

  sub(label: string): SubStaircase {
    const sc = this.subs.find((x) => x.label === label);
    if (!sc) throw new Error(`unknown staircase ${label}`);
    return sc;
  }

  subThreshold(sc: SubStaircase): number | null {
    if (sc instanceof QuestStaircase) return sc.threshold();
    const revs = this.reversalPresented.get(sc.label)!.slice(this.discardReversals);
    if (revs.length) return mean(revs);
    const shown = this.presented.get(sc.label)!;
    if (!shown.length) return null;
    return mean(shown.slice(Math.floor(shown.length / 2)));
  }

  subThresholds(): Record<string, number | null> {
    const out: Record<string, number | null> = {};
    for (const sc of this.subs) out[sc.label] = this.subThreshold(sc);
    return out;
  }

  threshold(): number | null {
    const vals = Object.values(this.subThresholds()).filter((v): v is number => v !== null);
    return vals.length ? mean(vals) : null;
  }

  spread(): number | null {
    const vals = Object.values(this.subThresholds()).filter((v): v is number => v !== null);
    if (vals.length < 2) return null;
    const m = mean(vals);
    let ss = 0;
    for (const v of vals) ss += (v - m) ** 2;
    return Math.sqrt(ss / (vals.length - 1));
  }

  get totalTrials(): number {
    return this.subs.reduce((n, sc) => n + sc.trialCount, 0);
  }

  get totalReversals(): number {
    return this.subs.reduce((n, sc) => n + sc.reversalCount, 0);
  }
}

function mean(vals: number[]): number {
  let s = 0;
  for (const v of vals) s += v;
  return s / vals.length;
}

// ---------------------------------------------------------------- numpy helpers

/** np.arange(start, stop, step) for floats: length ceil((stop-start)/step), start + i*delta. */
export function numpyArange(start: number, stop: number, step: number): Float64Array {
  const n = Math.max(0, Math.ceil((stop - start) / step));
  const delta = start + step - start;
  const out = new Float64Array(n);
  if (n > 0) out[0] = start;
  if (n > 1) out[1] = start + step;
  for (let i = 2; i < n; i++) out[i] = start + i * delta;
  return out;
}

export function numpyLinspace(start: number, stop: number, num: number): Float64Array {
  const out = new Float64Array(num);
  const step = (stop - start) / (num - 1);
  for (let i = 0; i < num; i++) out[i] = start + i * step;
  if (num > 1) out[num - 1] = stop;
  return out;
}

function maxOf(a: Float64Array): number {
  let m = -Infinity;
  for (let i = 0; i < a.length; i++) if (a[i]! > m || Number.isNaN(a[i]!)) m = a[i]!;
  return m;
}

function subtractMax(a: Float64Array): void {
  const m = maxOf(a);
  for (let i = 0; i < a.length; i++) a[i] = a[i]! - m;
}

function clip(x: number, lo: number, hi: number): number {
  return Math.min(Math.max(x, lo), hi);
}

/** numpy-style pairwise summation (blocks of 8 unrolled, recursive above 128). */
export function pairwiseSum(a: ArrayLike<number>, lo = 0, n = a.length): number {
  if (n < 8) {
    let s = 0;
    for (let i = lo; i < lo + n; i++) s += a[i]!;
    return s;
  }
  if (n <= 128) {
    const r = [a[lo]!, a[lo + 1]!, a[lo + 2]!, a[lo + 3]!, a[lo + 4]!, a[lo + 5]!, a[lo + 6]!, a[lo + 7]!];
    let i = 8;
    for (; i < n - (n % 8); i += 8) {
      for (let k = 0; k < 8; k++) r[k] = r[k]! + a[lo + i + k]!;
    }
    let res = ((r[0]! + r[1]!) + (r[2]! + r[3]!)) + ((r[4]! + r[5]!) + (r[6]! + r[7]!));
    for (; i < n; i++) res += a[lo + i]!;
    return res;
  }
  let n2 = Math.floor(n / 2);
  n2 -= n2 % 8;
  return pairwiseSum(a, lo, n2) + pairwiseSum(a, lo + n2, n - n2);
}

function dot(a: Float64Array, b: Float64Array): number {
  const prod = new Float64Array(a.length);
  for (let i = 0; i < a.length; i++) prod[i] = a[i]! * b[i]!;
  return pairwiseSum(prod);
}
